import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  LogOut,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  facciate: "Rifacimento Facciate",
  cappotto: "Cappotto Termico",
  finiture: "Finiture d'Interni",
  ristrutturazioni: "Ristrutturazione chiavi in mano",
  carpenteria: "Carpenteria Metallica",
  altro: "Altro / Sopralluogo",
};

const TIMING_LABELS = {
  urgente: "Urgente (≤30gg)",
  "1-3-mesi": "1-3 mesi",
  "3-6-mesi": "3-6 mesi",
  "da-valutare": "Da valutare",
};

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

function SourceBadge({ source }) {
  if (source === "whatsapp") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase bg-[#25D366] text-white px-2 py-0.5 font-bold">
        <MessageCircle size={10} /> WhatsApp
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[10px] tracking-wider uppercase bg-slate-700 text-white px-2 py-0.5 font-bold">
      Web
    </span>
  );
}

export default function Admin() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("preventivi");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      try {
        const [q, c] = await Promise.all([
          axios.get(`${API}/preventivi`),
          axios.get(`${API}/contatti`),
        ]);
        setQuotes(q.data);
        setContacts(c.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050B14] text-slate-500 flex items-center justify-center">
        Caricamento…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[#050B14] text-slate-100"
    >
      <header className="border-b border-[#1E293B] py-5 px-6 md:px-10 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a
            href="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 text-slate-400 hover:text-white text-sm"
          >
            <ArrowLeft size={16} /> Sito
          </a>
          <div className="h-6 w-px bg-slate-700" />
          <div>
            <div className="font-bold text-white">Area Riservata</div>
            <div className="text-xs text-slate-500 tracking-wider uppercase">
              Dashboard richieste
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:block text-right">
            <div className="text-sm text-white font-semibold">{user.name}</div>
            <div className="text-[10px] tracking-wider uppercase text-slate-500">
              {user.email}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-slate-700 hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-slate-300 px-4 py-2 text-xs font-semibold transition-colors"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <div className="bg-[#0A1120] border border-[#1E293B] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Preventivi
            </div>
            <div className="text-3xl font-bold text-white tabular">
              {quotes.length}
            </div>
          </div>
          <div className="bg-[#0A1120] border border-[#1E293B] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Da WhatsApp
            </div>
            <div className="text-3xl font-bold text-[#25D366] tabular">
              {quotes.filter((q) => q.source === "whatsapp").length}
            </div>
          </div>
          <div className="bg-[#0A1120] border border-[#1E293B] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Contatti
            </div>
            <div className="text-3xl font-bold text-white tabular">
              {contacts.length}
            </div>
          </div>
          <div className="bg-[#0A1120] border border-[#1E293B] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Stato
            </div>
            <div className="text-lg font-bold text-[#22C55E]">● Online</div>
          </div>
        </div>

        <div className="flex gap-1 mb-6">
          <button
            data-testid="tab-preventivi"
            onClick={() => setTab("preventivi")}
            className={`px-5 py-3 text-sm font-semibold ${
              tab === "preventivi"
                ? "bg-[#FF5A1F] text-white"
                : "bg-[#0A1120] text-slate-400 hover:text-white"
            }`}
          >
            Preventivi ({quotes.length})
          </button>
          <button
            data-testid="tab-contatti"
            onClick={() => setTab("contatti")}
            className={`px-5 py-3 text-sm font-semibold ${
              tab === "contatti"
                ? "bg-[#FF5A1F] text-white"
                : "bg-[#0A1120] text-slate-400 hover:text-white"
            }`}
          >
            Contatti ({contacts.length})
          </button>
        </div>

        {loading && (
          <div className="text-slate-500 py-12 text-center">Caricamento…</div>
        )}

        {!loading && tab === "preventivi" && (
          <div className="space-y-3">
            {quotes.length === 0 ? (
              <div className="bg-[#0A1120] border border-[#1E293B] p-10 text-center text-slate-500">
                Nessun preventivo ricevuto al momento.
              </div>
            ) : (
              quotes.map((q) => (
                <article
                  key={q.id}
                  data-testid={`quote-row-${q.id}`}
                  className="bg-[#0A1120] border border-[#1E293B] p-6 grid md:grid-cols-12 gap-4"
                >
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-2 mb-2">
                      <SourceBadge source={q.source} />
                      <span className="text-xs tracking-wider uppercase text-[#FF5A1F] font-bold">
                        {SERVICE_LABELS[q.servizio] || q.servizio}
                      </span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {q.nome} {q.cognome}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Calendar size={12} /> {fmtDate(q.timestamp)}
                    </div>
                  </div>
                  <div className="md:col-span-3 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail size={14} className="text-slate-500" />
                      <a
                        href={`mailto:${q.email}`}
                        className="hover:text-[#FF5A1F] break-all"
                      >
                        {q.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Phone size={14} className="text-slate-500" />
                      <a
                        href={`tel:${q.telefono}`}
                        className="hover:text-[#FF5A1F]"
                      >
                        {q.telefono}
                      </a>
                    </div>
                    {q.citta && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <MapPin size={14} className="text-slate-500" />
                        {q.citta}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                      Tempistica
                    </div>
                    <div className="text-white text-sm font-semibold">
                      {TIMING_LABELS[q.tempistica] || q.tempistica}
                    </div>
                  </div>
                  <div className="md:col-span-4">
                    {q.messaggio && (
                      <>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                          Messaggio
                        </div>
                        <div className="text-slate-300 text-sm leading-relaxed">
                          {q.messaggio}
                        </div>
                      </>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {!loading && tab === "contatti" && (
          <div className="space-y-3">
            {contacts.length === 0 ? (
              <div className="bg-[#0A1120] border border-[#1E293B] p-10 text-center text-slate-500">
                Nessun messaggio ricevuto al momento.
              </div>
            ) : (
              contacts.map((c) => (
                <article
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  className="bg-[#0A1120] border border-[#1E293B] p-6 grid md:grid-cols-12 gap-4"
                >
                  <div className="md:col-span-3">
                    <div className="text-white font-bold text-lg">
                      {c.nome}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Calendar size={12} /> {fmtDate(c.timestamp)}
                    </div>
                  </div>
                  <div className="md:col-span-3 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail size={14} className="text-slate-500" />
                      <a
                        href={`mailto:${c.email}`}
                        className="hover:text-[#FF5A1F]"
                      >
                        {c.email}
                      </a>
                    </div>
                    {c.telefono && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <Phone size={14} className="text-slate-500" />
                        {c.telefono}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-6">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                      Messaggio
                    </div>
                    <div className="text-slate-300 text-sm leading-relaxed">
                      {c.messaggio}
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
